import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { IconRefresh, IconAlertTriangle } from '@tabler/icons-react';
import type { Vendor, VendorImportBatch, VendorImportRenewalRow } from '@techgitter/contracts';
import { getVendors } from '../../services/vendorService';
import { fetchRenewalRows, fetchImportBatches } from '../../services/reconciliationApi';
import { textMatches } from './reconciliationFilter';
import { ReconciliationFilterFields } from './ReconciliationFilterFields';
import {
  SecondaryToolbar,
  ToolbarSelect,
  ToolbarChip,
  ToolbarSpacer,
  ToolbarButton,
} from '../toolbar';
import {
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
} from '../table';
import { Chip } from '../chip';
import { CARD_SX } from '../../styles/designSystem';

const RENEWAL_BATCH_SOURCE = 'm365_renewals';

/**
 * The Renewals area: forward-looking renewal rows (Control 3) for a supplier's
 * latest renewal import. Each row pairs the CURRENT subscription state with the
 * SCHEDULED state it renews into; where the scheduled state differs from a
 * commitment, a renewal variance is opened (surfaced on the Variances tab). A
 * trial's current block is parked (never a variance) but shown here for context.
 * Read-only. See docs/architecture/renewal-reconciliation-control.md.
 */
interface Props {
  customerFilter?: string;
  productFilter?: string;
  onCustomerFilterChange?: (v: string) => void;
  onProductFilterChange?: (v: string) => void;
}

export const RenewalRowsPanel: React.FC<Props> = ({
  customerFilter = '',
  productFilter = '',
  onCustomerFilterChange,
  onProductFilterChange,
}) => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorId, setVendorId] = useState<string>('');
  const [batch, setBatch] = useState<VendorImportBatch | null>(null);
  const [rows, setRows] = useState<VendorImportRenewalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imminentOnly, setImminentOnly] = useState(false);
  const [sortKey, setSortKey] = useState<'expiry' | 'customer'>('expiry');

  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: v.vendorId, label: v.name })),
    [vendors],
  );

  useEffect(() => {
    const controller = new AbortController();
    getVendors(controller.signal)
      .then((all) => {
        if (controller.signal.aborted) return;
        const suppliers = all.filter((v) => v.isSubscriptionSupplier);
        setVendors(suppliers);
        if (suppliers.length && !vendorId) setVendorId(suppliers[0].vendorId);
      })
      .catch((e) => {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError('Could not load subscription suppliers.');
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!vendorId) return;
      setLoading(true);
      setError(null);
      try {
        const [batches, renewalRows] = await Promise.all([
          fetchImportBatches({ vendorId, top: 20 }, signal),
          fetchRenewalRows({ vendorId }, signal),
        ]);
        if (signal?.aborted) return;
        setBatch(batches.find((b) => b.source === RENEWAL_BATCH_SOURCE) ?? null);
        setRows(renewalRows);
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load renewal rows');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [vendorId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const sorted = useMemo(() => {
    const byCustomer = (a: VendorImportRenewalRow, b: VendorImportRenewalRow) =>
      (a.customerIdentifier ?? '').localeCompare(b.customerIdentifier ?? '');
    // Undated / unparseable expiries sort last under expiry sort.
    const expiryTs = (v?: string | null) => {
      if (!v) return Infinity;
      const t = new Date(v).getTime();
      return Number.isNaN(t) ? Infinity : t;
    };
    const arr = rows.filter(
      (r) =>
        textMatches(customerFilter, r.customerIdentifier) &&
        textMatches(productFilter, r.sku, r.scheduledPlan),
    );
    if (sortKey === 'expiry') {
      arr.sort((a, b) => expiryTs(a.currentEndDate) - expiryTs(b.currentEndDate) || byCustomer(a, b));
    } else {
      arr.sort(byCustomer);
    }
    return arr;
  }, [rows, sortKey, customerFilter, productFilter]);
  const visible = useMemo(
    () => (imminentOnly ? sorted.filter((r) => r.imminent) : sorted),
    [sorted, imminentOnly],
  );

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarSelect
          label="Supplier"
          value={vendorId}
          onChange={setVendorId}
          options={vendorOptions}
        />
        {batch && (
          <Typography sx={{ fontSize: 12, color: '#888' }}>
            Latest renewal import: {fmtDate(batch.importedAt)} · {batch.rowCount ?? '—'} rows
          </Typography>
        )}
        <ToolbarSelect
          label="Sort"
          value={sortKey}
          onChange={(v) => setSortKey(v as 'expiry' | 'customer')}
          options={[
            { value: 'expiry', label: 'Expiry (soonest)' },
            { value: 'customer', label: 'Customer' },
          ]}
        />
        <ReconciliationFilterFields
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={onCustomerFilterChange ?? (() => {})}
          onProductFilterChange={onProductFilterChange ?? (() => {})}
        />
        <ToolbarChip
          label="Imminent only"
          active={imminentOnly}
          onClick={() => setImminentOnly((v) => !v)}
          icon={IconAlertTriangle}
        />
        <ToolbarSpacer />
        <ToolbarButton icon={IconRefresh} onClick={() => load()}>
          Refresh
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>
          Forward-looking renewals (Control 3): each row pairs the current subscription with the
          state it renews into. Where the scheduled state differs from a commitment, a renewal
          variance is opened (see the Variances tab). Read-only.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : visible.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: '#888', px: 0.5, py: 2 }}>
            {sorted.length === 0
              ? 'No renewal rows in this supplier’s latest renewal import.'
              : 'No imminent renewals — turn off “Imminent only” to show all.'}
          </Typography>
        ) : (
          <Box sx={[CARD_SX, { overflow: 'hidden' }]}>
            <Box sx={{ p: '12px 16px', borderBottom: '0.5px solid #e2eaf3', display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Renewals</Typography>
              <Chip>{visible.length}</Chip>
            </Box>
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Customer</DataTableHeadCell>
                  <DataTableHeadCell>Current</DataTableHeadCell>
                  <DataTableHeadCell>Renews into</DataTableHeadCell>
                  <DataTableHeadCell>Expires</DataTableHeadCell>
                  <DataTableHeadCell>Renews</DataTableHeadCell>
                  <DataTableHeadCell>Flags</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <DataTableRow key={r.id}>
                    <DataTableCell>{r.customerIdentifier ?? '—'}</DataTableCell>
                    <DataTableCell>
                      <Typography sx={{ fontSize: 13, color: '#333' }}>{r.sku ?? '—'}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#888' }}>
                        {r.currentSeats ?? '—'} seats{r.currentBillingTerm ? ` · ${r.currentBillingTerm}` : ''}
                      </Typography>
                    </DataTableCell>
                    <DataTableCell>
                      <Typography sx={{ fontSize: 13, color: '#333' }}>{r.scheduledPlan ?? '—'}</Typography>
                      <Typography sx={{ fontSize: 11, color: '#888' }}>
                        {r.scheduledSeats ?? '—'} seats{r.scheduledBillingTerm ? ` · ${r.scheduledBillingTerm}` : ''}
                      </Typography>
                    </DataTableCell>
                    <DataTableCell muted>{fmtDate(r.currentEndDate)}</DataTableCell>
                    <DataTableCell muted>
                      {fmtDate(r.scheduledEndDate)}
                      {typeof r.daysRemaining === 'number' ? ` · ${r.daysRemaining}d` : ''}
                    </DataTableCell>
                    <DataTableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {r.imminent && (
                          <Chip tone="warning" title="Renews within the imminence window">imminent</Chip>
                        )}
                        {r.isTrial && (
                          <Chip title="Current block is a free trial — parked from renewal variances">trial</Chip>
                        )}
                        {r.trialAutoRenews && (
                          <Chip tone="warning" title="Trial converting to a paid plan">converting</Chip>
                        )}
                      </Box>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </tbody>
            </DataTable>
          </Box>
        )}
      </Box>
    </>
  );
};

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}
